/**
 * Shared UI primitives — v2 light theme (Stripe / Linear / Ramp inspired).
 *
 * These coexist with the legacy dark primitives in `index.tsx`. Pages
 * that have been migrated to the new design system import from here
 * and add `theme-light` to their root container.
 */

import {
  type ReactNode,
  type ButtonHTMLAttributes,
  type HTMLAttributes,
  type InputHTMLAttributes,
  type TextareaHTMLAttributes,
  forwardRef,
} from 'react';
import { Loader2, ArrowLeft } from 'lucide-react';

// ── PageHeader ────────────────────────────────────────────────────────────

interface PageHeaderProps {
  title: string;
  subtitle?: string;
  eyebrow?: string;
  actions?: ReactNode;
  back?: { label: string; onClick: () => void };
}

export function PageHeader({ title, subtitle, eyebrow, actions, back }: PageHeaderProps) {
  return (
    <div className="flex items-start justify-between gap-4 mb-8">
      <div className="min-w-0">
        {back && (
          <button
            onClick={back.onClick}
            className="l-btn l-btn-ghost l-btn-sm -ml-2 mb-2"
          >
            <ArrowLeft className="w-4 h-4" />
            {back.label}
          </button>
        )}
        {eyebrow && <div className="l-section-eyebrow mb-2">{eyebrow}</div>}
        <h1 className="l-display-3 truncate" style={{ color: 'var(--ch2-ink)' }}>
          {title}
        </h1>
        {subtitle && (
          <p className="l-muted text-sm mt-1.5 max-w-2xl">{subtitle}</p>
        )}
      </div>
      {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
    </div>
  );
}

// ── SectionCard ───────────────────────────────────────────────────────────

interface SectionCardProps extends HTMLAttributes<HTMLDivElement> {
  title?: string;
  subtitle?: string;
  eyebrow?: string;
  actions?: ReactNode;
  children: ReactNode;
  noPadding?: boolean;
  variant?: 'default' | 'flat' | 'muted' | 'elevated';
}

export function SectionCard({
  title,
  subtitle,
  eyebrow,
  actions,
  children,
  noPadding,
  variant = 'default',
  className = '',
  ...rest
}: SectionCardProps) {
  const variantClass =
    variant === 'flat'     ? 'l-card-flat' :
    variant === 'muted'    ? 'l-card-muted' :
    variant === 'elevated' ? 'l-card-elevated' :
    'l-card';
  return (
    <div className={`${variantClass} ${className}`} {...rest}>
      {(title || actions) && (
        <div
          className="flex items-center justify-between px-6 py-4"
          style={{ borderBottom: '1px solid var(--ch2-border-2)' }}
        >
          <div className="min-w-0">
            {eyebrow && <div className="l-section-eyebrow mb-1">{eyebrow}</div>}
            {title && (
              <h2
                className="font-display font-semibold tracking-tight"
                style={{ fontSize: '16px', color: 'var(--ch2-ink)' }}
              >
                {title}
              </h2>
            )}
            {subtitle && (
              <p className="l-muted text-sm mt-0.5">{subtitle}</p>
            )}
          </div>
          {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
        </div>
      )}
      <div className={noPadding ? '' : 'p-6'}>{children}</div>
    </div>
  );
}

// ── StatCard ──────────────────────────────────────────────────────────────

interface StatCardProps {
  label: string;
  value: string | number;
  trend?: { direction: 'up' | 'down' | 'flat'; value: string };
  hint?: string;
  icon?: ReactNode;
}

export function StatCard({ label, value, trend, hint, icon }: StatCardProps) {
  return (
    <div className="l-stat">
      <div className="flex items-start justify-between">
        <div className="l-stat-label">{label}</div>
        {icon && <div style={{ color: 'var(--ch2-ink-3)' }}>{icon}</div>}
      </div>
      <div className="l-stat-value">{value}</div>
      <div className="flex items-center gap-2 mt-2">
        {trend && (
          <span
            className="text-sm font-medium"
            style={{
              color:
                trend.direction === 'up'   ? 'var(--ch2-success)' :
                trend.direction === 'down' ? 'var(--ch2-danger)'  :
                'var(--ch2-ink-3)',
            }}
          >
            {trend.direction === 'up' ? '↑' : trend.direction === 'down' ? '↓' : '→'} {trend.value}
          </span>
        )}
        {hint && <span className="l-subtle text-sm">{hint}</span>}
      </div>
    </div>
  );
}

// ── Button ────────────────────────────────────────────────────────────────

type ButtonVariant = 'primary' | 'accent' | 'secondary' | 'ghost' | 'danger';
type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', loading, iconLeft, iconRight, className = '', children, disabled, ...rest },
  ref
) {
  const variantClass =
    variant === 'accent'    ? 'l-btn-accent' :
    variant === 'secondary' ? 'l-btn-secondary' :
    variant === 'ghost'     ? 'l-btn-ghost' :
    variant === 'danger'    ? 'l-btn-danger' :
    'l-btn-primary';
  const sizeClass = size === 'sm' ? 'l-btn-sm' : size === 'lg' ? 'l-btn-lg' : '';
  return (
    <button
      ref={ref}
      disabled={disabled || loading}
      className={`l-btn ${variantClass} ${sizeClass} ${className}`}
      {...rest}
    >
      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : iconLeft}
      {children}
      {!loading && iconRight}
    </button>
  );
});

// ── Input ─────────────────────────────────────────────────────────────────

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  hint?: string;
  error?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, hint, error, className = '', id, ...rest },
  ref
) {
  return (
    <div className="space-y-1.5">
      {label && (
        <label
          htmlFor={id}
          className="block text-sm font-medium"
          style={{ color: 'var(--ch2-ink)' }}
        >
          {label}
        </label>
      )}
      <input
        id={id}
        ref={ref}
        className={`l-input ${error ? 'border-red-500' : ''} ${className}`}
        style={error ? { borderColor: 'var(--ch2-danger)' } : undefined}
        {...rest}
      />
      {error ? (
        <p className="text-sm" style={{ color: 'var(--ch2-danger)' }}>{error}</p>
      ) : hint ? (
        <p className="l-subtle text-sm">{hint}</p>
      ) : null}
    </div>
  );
});

interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  hint?: string;
  error?: string;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { label, hint, error, className = '', id, ...rest },
  ref
) {
  return (
    <div className="space-y-1.5">
      {label && (
        <label
          htmlFor={id}
          className="block text-sm font-medium"
          style={{ color: 'var(--ch2-ink)' }}
        >
          {label}
        </label>
      )}
      <textarea
        id={id}
        ref={ref}
        className={`l-textarea ${error ? 'border-red-500' : ''} ${className}`}
        style={error ? { borderColor: 'var(--ch2-danger)' } : undefined}
        {...rest}
      />
      {error ? (
        <p className="text-sm" style={{ color: 'var(--ch2-danger)' }}>{error}</p>
      ) : hint ? (
        <p className="l-subtle text-sm">{hint}</p>
      ) : null}
    </div>
  );
});

// ── Checkbox ──────────────────────────────────────────────────────────────

interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: ReactNode;
  hint?: ReactNode;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { label, hint, className = '', id, ...rest },
  ref
) {
  return (
    <label htmlFor={id} className="flex items-start gap-3 cursor-pointer select-none">
      <input
        id={id}
        ref={ref}
        type="checkbox"
        className={`l-checkbox mt-0.5 ${className}`}
        {...rest}
      />
      <span className="text-sm leading-relaxed" style={{ color: 'var(--ch2-ink)' }}>
        {label}
        {hint && (
          <span className="block l-subtle text-xs mt-0.5">{hint}</span>
        )}
      </span>
    </label>
  );
});

// ── Badge ─────────────────────────────────────────────────────────────────

type BadgeVariant = 'neutral' | 'accent' | 'success' | 'warning' | 'danger' | 'info' | 'locked';

interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  variant?: BadgeVariant;
  children: ReactNode;
}

export function Badge({ variant = 'neutral', className = '', children, ...rest }: BadgeProps) {
  return (
    <span className={`l-badge-${variant} ${className}`} {...rest}>
      {children}
    </span>
  );
}

// ── EmptyState ────────────────────────────────────────────────────────────

interface EmptyStateProps {
  icon?: ReactNode;
  title: string;
  description?: string;
  action?: ReactNode;
}

export function EmptyState({ icon, title, description, action }: EmptyStateProps) {
  return (
    <div
      className="flex flex-col items-center justify-center text-center px-6 py-16"
      style={{
        background: 'var(--ch2-surface-2)',
        border: '1px dashed var(--ch2-border)',
        borderRadius: 'var(--ch2-radius-md)',
      }}
    >
      {icon && (
        <div
          className="w-12 h-12 rounded-full flex items-center justify-center mb-4"
          style={{ background: 'var(--ch2-surface)', color: 'var(--ch2-ink-3)' }}
        >
          {icon}
        </div>
      )}
      <h3
        className="font-display font-semibold"
        style={{ fontSize: '16px', color: 'var(--ch2-ink)' }}
      >
        {title}
      </h3>
      {description && (
        <p className="l-muted text-sm mt-1.5 max-w-md">{description}</p>
      )}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

// ── LockedTeaser ──────────────────────────────────────────────────────────

interface LockedTeaserProps {
  title: string;
  description?: string;
  bullets?: string[];
  cta?: { label: string; onClick?: () => void; href?: string };
}

export function LockedTeaser({ title, description, bullets, cta }: LockedTeaserProps) {
  const Inner = (
    <button
      type="button"
      onClick={cta?.onClick}
      className="l-btn l-btn-accent l-btn-sm"
    >
      {cta?.label ?? 'Unlock'}
    </button>
  );
  return (
    <div className="l-lock-card p-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-2">
            <Badge variant="locked">Premium</Badge>
            <span className="l-subtle text-xs">Pro & Scale</span>
          </div>
          <h3
            className="font-display font-semibold mb-1"
            style={{ fontSize: '17px', color: 'var(--ch2-ink)' }}
          >
            {title}
          </h3>
          {description && <p className="l-muted text-sm">{description}</p>}
          {bullets && bullets.length > 0 && (
            <ul className="mt-4 space-y-1.5">
              {bullets.map((b) => (
                <li key={b} className="flex items-start gap-2 text-sm">
                  <span
                    className="mt-1 w-1 h-1 rounded-full shrink-0"
                    style={{ background: 'var(--ch2-accent)' }}
                  />
                  <span style={{ color: 'var(--ch2-ink-2)' }}>{b}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        {cta &&
          (cta.href ? (
            <a href={cta.href} className="l-btn l-btn-accent l-btn-sm">
              {cta.label}
            </a>
          ) : (
            Inner
          ))}
      </div>
    </div>
  );
}

// ── Skeleton ──────────────────────────────────────────────────────────────

export function Skeleton({ className = '', height = 16 }: { className?: string; height?: number | string }) {
  return (
    <div
      className={className}
      style={{
        height,
        background: 'linear-gradient(90deg, var(--ch2-surface-2) 25%, var(--ch2-surface-3) 50%, var(--ch2-surface-2) 75%)',
        backgroundSize: '200% 100%',
        animation: 'shimmer 1.5s infinite',
        borderRadius: 'var(--ch2-radius-sm)',
      }}
    />
  );
}

// ── Section nav (sticky tabs for the report viewer) ───────────────────────

interface SectionNavProps {
  sections: Array<{ id: string; label: string; locked?: boolean }>;
  activeId?: string;
  onSelect?: (id: string) => void;
}

export function SectionNav({ sections, activeId, onSelect }: SectionNavProps) {
  return (
    <nav
      className="flex gap-1 overflow-x-auto hide-scrollbar"
      role="tablist"
      aria-label="Report sections"
    >
      {sections.map((s) => {
        const active = s.id === activeId;
        return (
          <button
            key={s.id}
            role="tab"
            aria-selected={active}
            onClick={() => onSelect?.(s.id)}
            className="px-3 py-2 text-sm font-medium whitespace-nowrap"
            style={{
              color: active ? 'var(--ch2-ink)' : 'var(--ch2-ink-2)',
              background: active ? 'var(--ch2-surface-2)' : 'transparent',
              borderRadius: 'var(--ch2-radius-sm)',
              transition: 'background 0.15s ease',
            }}
          >
            {s.label}
            {s.locked && (
              <span className="ml-1.5 inline-block align-middle">
                <Badge variant="locked">PRO</Badge>
              </span>
            )}
          </button>
        );
      })}
    </nav>
  );
}
